import type { SalesTeamsMemberRecord } from "@graftus/schema";

/**
 * Repairs common malformed member mentions from the model:
 * - Multi-line "chip style" output: `**@\\nCH\\nCharlotte\\n(member:id):**`
 * - Whitespace/newlines between `@` and `[` or between `]` and `(member:`
 * - Newlines / runs of whitespace inside `[...]` (so block parsing does not split the token)
 * Collapses internal label whitespace to single spaces; does not change already-normal names.
 *
 * Member chips are rendered by splitting at `@[…](member:…)`; wrapping that token in `**…**`
 * would orphan the asterisks in adjacent text chunks (inline markdown bold must stay in one chunk).
 * So multiline repairs emit a plain token, and any `**@[…](member:…)**` from the model is unwrapped.
 */
export function normalizeBrokenMemberMentions(text: string): string {
  const withBrackets = text
    .replace(/(?<![\w/])@\s+\[/g, "@[")
    .replace(/\]\s*\(\s*member:/g, "](member:")
    .replace(/@\[([^\]]+)\]\(member:/g, (_m, inner: string) => {
      const clean = inner.replace(/\s+/g, " ").trim();
      return `@[${clean}](member:`;
    });

  const multilineBroken =
    /(\*\*)?(?<![\w/])@(?!\[)\s*(?:\r?\n\s*)+(?:([A-Z]{2,4})\s*(?:\r?\n\s*))?([^\n()]+?)\s*(?:\r?\n\s*)+\(\s*member:([a-zA-Z0-9_-]+)\s*\)(?:[^\S\r\n]*:\*\*|[^\S\r\n]*\*\*)?/g;

  const repaired = withBrackets.replace(
    multilineBroken,
    (_full, _bold: string | undefined, _initials: string | undefined, name: string, id: string) => {
      const cleanName = name.replace(/\s+/g, " ").trim();
      return `@[${cleanName}](member:${id})`;
    },
  );

  return repaired.replace(
    /\*\*(@\[[^\]]+\]\(member:[^)\s]+\))\*\*/g,
    "$1",
  );
}

/** Strips internal context markers if the model echoes them (defense-in-depth). */
export function stripIntelligenceLeaks(text: string): string {
  return text.replace(/\[memberId=[^\]]+\]/g, "");
}

/**
 * Removes Storage download URLs the model copied into running text.
 *
 * Download links (action-plan PDFs) are rendered by the tool-call widget from
 * the tool output verbatim; a model-transcribed copy of the same ~250-char
 * tokenized URL is unreliable (one corrupted character = "Permission denied"),
 * so any Storage URL in the text is dead weight at best and a broken duplicate
 * of the working button at worst. Markdown links keep their label text.
 */
export function stripModelCopiedStorageLinks(text: string): string {
  const storageUrl =
    /https:\/\/(?:firebasestorage|storage)\.googleapis\.com\/[^\s)\]]*/;
  return text
    .replace(
      new RegExp(String.raw`\[([^\]]*)\]\(\s*${storageUrl.source}\s*\)`, "g"),
      "$1",
    )
    .replace(new RegExp(storageUrl.source, "g"), "");
}

export type IntelligenceMemberRoster = ReadonlyMap<
  string,
  SalesTeamsMemberRecord
>;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Names the roster can link, longest first so "Emma Persson" wins over
 * "Emma". A first name alone counts only when exactly one roster member has
 * it, so "Emma" never points at the wrong Emma.
 */
function rosterNamePatterns(
  roster: IntelligenceMemberRoster,
): { pattern: string; memberId: string; name: string }[] {
  const firstNameOwners = new Map<string, string[]>();
  const out: { pattern: string; memberId: string; name: string }[] = [];
  for (const [memberId, member] of roster) {
    const full = member.name.replace(/\s+/g, " ").trim();
    if (full.length < 2) continue;
    out.push({ pattern: escapeRegExp(full).replace(/ /g, "\\s+"), memberId, name: full });
    const first = full.split(" ")[0]!;
    if (first.length >= 3) {
      const key = first.toLocaleLowerCase("sv-SE");
      firstNameOwners.set(key, [...(firstNameOwners.get(key) ?? []), memberId]);
    }
  }
  for (const [memberId, member] of roster) {
    const full = member.name.replace(/\s+/g, " ").trim();
    const first = full.split(" ")[0]!;
    if (first.length < 3) continue;
    const owners = firstNameOwners.get(first.toLocaleLowerCase("sv-SE")) ?? [];
    if (owners.length === 1 && full !== first) {
      out.push({ pattern: escapeRegExp(first), memberId, name: full });
    }
  }
  return out.sort((a, b) => b.name.length - a.name.length);
}

/** Text that must not be touched: existing mention tags, inline code, link targets, URLs. */
const PROTECTED_SEGMENT =
  /(<mention[^>]*>[\s\S]*?<\/mention>|`[^`\n]*`|\]\([^)\n]*\)|https?:\/\/[^\s)]+)/g;

/**
 * Links plain roster names in `text` as `<mention mid="…">Name</mention>`,
 * the same tag `@[…](member:…)` tokens are converted to, so a seller gets the
 * pill whether or not the model remembered the token. Matches whole words,
 * case-insensitive, and leaves a Swedish genitive "s" outside the pill
 * ("Emmas möten" → [Emma]s möten). While streaming, the trailing unfinished
 * word is left alone so "Emma" does not flash into a pill before " Persson"
 * arrives.
 */
export function linkRosterMentions(
  text: string,
  roster: IntelligenceMemberRoster | null | undefined,
  opts: { isStreaming?: boolean } = {},
): string {
  if (!roster || roster.size === 0 || !text) return text;
  const patterns = rosterNamePatterns(roster);
  if (patterns.length === 0) return text;
  const alternation = patterns.map((p) => `(${p.pattern})`).join("|");
  // Unicode letter boundaries: "Emma" must not match inside "Gemma", and a
  // trailing possessive "s" is allowed after the name.
  const matcher = new RegExp(
    `(?<![\\p{L}\\p{N}])(?:${alternation})(?=s?(?![\\p{L}\\p{N}]))`,
    "giu",
  );

  let safeEnd = text.length;
  if (opts.isStreaming) {
    const lastBreak = Math.max(text.lastIndexOf(" "), text.lastIndexOf("\n"));
    safeEnd = lastBreak === -1 ? 0 : lastBreak;
  }
  const head = text.slice(0, safeEnd);
  const tail = text.slice(safeEnd);

  const linked = head
    .split(PROTECTED_SEGMENT)
    .map((segment, index) => {
      if (index % 2 === 1 || !segment) return segment;
      return segment.replace(matcher, (match, ...groups: unknown[]) => {
        const hit = patterns.findIndex((_, i) => typeof groups[i] === "string");
        const pattern = hit === -1 ? null : patterns[hit];
        if (!pattern) return match;
        return `<mention mid="${pattern.memberId}">${pattern.name}</mention>`;
      });
    })
    .join("");
  return linked + tail;
}

/** True if the string still contains a partial `@[...](member:...)` token (streaming). */
export function hasIncompleteMentionSuffix(s: string): boolean {
  if (!s.includes("@[")) return false;
  const stripped = s.replace(/@\[([^\]]+)\]\(member:[^)\s]+\)/g, "");
  return stripped.includes("@[");
}
